import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { coerceLinkPayload, getLinkPreviewIcon } from "../../link-preview-utils";
import type { RendererProps } from "../types";

import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import type { LinkPayload } from "@/routes/smart-table/utils/contentType";

const LinkPreviewHeader = ({ link }: { link: LinkPayload }) => {
  const Icon = getLinkPreviewIcon(link.kind);

  return (
    <div className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm ring-1 ring-border/50">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{link.label}</p>
          <p className="truncate text-xs text-muted-foreground">{link.url}</p>
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        <CopyButton
          text={link.url}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        />
        <Button type="button" variant="ghost" size="tinyIcon" asChild>
          <a href={link.url} target="_blank" rel="noreferrer" aria-label="Open link in new tab">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </Button>
      </div>
    </div>
  );
};

export function LinkRenderer({ content }: RendererProps) {
  const [previewError, setPreviewError] = useState<string | null>(null);
  const link = coerceLinkPayload({
    rawValue: content.rawValue,
    displayPreview: content.displayPreview,
    linkPayload: content.linkPayload,
  });

  if (!link) {
    return <span className="italic text-muted-foreground">No link available</span>;
  }

  const preview = (() => {
    if (link.kind === "image") {
      return (
        <div className="flex justify-center rounded-xl border border-border/40 bg-muted/10 p-3 shadow-sm">
          <img
            src={link.url}
            alt={link.label}
            className="max-h-[60vh] max-w-full rounded-lg object-contain"
            onError={() => setPreviewError("Image preview unavailable")}
            onLoad={() => setPreviewError(null)}
          />
        </div>
      );
    }

    if (link.kind === "video") {
      return (
        <div className="rounded-xl border border-border/40 bg-muted/10 p-3 shadow-sm">
          <video
            src={link.url}
            controls
            className="max-h-[60vh] w-full rounded-lg outline-none"
            onError={() => setPreviewError("Video preview unavailable")}
            onLoadedData={() => setPreviewError(null)}
          >
            <track kind="captions" />
          </video>
        </div>
      );
    }

    if (link.kind === "audio") {
      return (
        <div className="rounded-xl border border-border/40 bg-muted/10 p-4 shadow-sm">
          <audio
            src={link.url}
            controls
            className="w-full outline-none"
            onError={() => setPreviewError("Audio preview unavailable")}
            onLoadedData={() => setPreviewError(null)}
          >
            <track kind="captions" />
          </audio>
        </div>
      );
    }

    if (link.kind === "pdf") {
      return (
        <div className="rounded-xl border border-border/40 bg-muted/10 p-3 shadow-sm">
          <iframe
            src={link.url}
            className="h-[60vh] w-full rounded-lg bg-white"
            title={link.label}
          />
        </div>
      );
    }

    return null;
  })();

  return (
    <div className="w-full max-w-3xl space-y-3">
      <LinkPreviewHeader link={link} />
      {preview}
      {previewError && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {previewError}. You can still open or copy the URL.
        </p>
      )}
    </div>
  );
}
